import { convertFromRaw, RawDraftContentState } from 'draft-js';
import { FieldArray, FormikHelpers, FormikValues } from 'formik';
import React, { useMemo, useState } from 'react';
import { array, mixed, object, string } from 'yup';

import { TarefaAdicionar, TarefaInformacoes } from 'Component/Admin/Tarefa';
import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Form, {
  CaixaDeSelecao,
  CaixaDeTexto,
  Campo,
  Contexto,
  DropZone,
  Selecao,
  Editor,
} from 'Component/Form';
import Icone from 'Component/Icone';
import Modal from 'Component/Modal';
import Alternativa from 'Interface/Alternativas';
import Bibliografia from 'Interface/Bibliografias';
import DicaMais from 'Interface/DicasMais';
import Quiz from 'Interface/Quizzes';
import Tarefa, {
  ProducaoDeUA as ProducaoDeUAInterface,
} from 'Interface/Tarefas';
import { useRecuperarCategoriasQuery } from 'Service';
import { useAppSelector } from 'Store';
import { IMAGENS, POWERPOINT } from 'View/constants';
import {
  useGetUnitDescriptionMutation,
  useGetUnitQuizzesMutation,
  useGetUnitSkillsMutation,
} from 'View/Service/Default/Eva';
import { useEditarTarefaMutation } from 'View/Service/Default/Tarefas';

interface Props {
  tarefa: Tarefa;
}

const regras1 = object({
  powerpoint: mixed()
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return POWERPOINT.includes(valor.type);
      }

      return true;
    })
    .required('Obrigatório'),
});

const regras2 = object({
  descricao: mixed().test('obrigatorio', 'Obrigatório', (valor) => {
    if (valor?.blocks?.length && valor.blocks[0].text) {
      return true;
    }

    return false;
  }),
  competencias: mixed().test('obrigatorio', 'Obrigatório', (valor) => {
    if (valor?.blocks?.length && valor.blocks[0].text) {
      return true;
    }

    return false;
  }),

});

const regras3 = object({
  quizzes: array()
    .of(
      object().shape({
        enunciado: string()
          .test(
            'correta',
            'Selecione a resposta correta',
            (valor, contexto) => {
              let selecionou = false;

              contexto.parent.alternativas.map((alternativa: Alternativa) =>
                alternativa.correta ? (selecionou = true) : null
              );

              return selecionou;
            }
          )
          .test(
            'correta',
            'Existe mais de uma resposta correta',
            (valor, contexto) => {
              let corretas = 0;

              contexto.parent.alternativas.map((alternativa: Alternativa) =>
                alternativa.correta ? corretas++ : null
              );

              return corretas <= 1;
            }
          )
          .test(
            'semnumeral',
            'Não deve conter nenhum prefixo como: 1., 2 -, A -...',
            (valor) => {
              if (
                valor?.startsWith('1') ||
                valor?.startsWith('2') ||
                valor?.startsWith('3') ||
                valor?.startsWith('4') ||
                valor?.startsWith('5') ||
                valor?.startsWith('.') ||
                valor?.startsWith('-')
              ) {
                return false;
              }

              return true;
            }
          )
          .required('Obrigatório'),
        imagem: mixed()
          .test('tipo', 'Formato não suportado', (valor) => {
            if (valor && valor.type) {
              return IMAGENS.includes(valor.type);
            }

            return true;
          })
          .optional(),
        alternativas: array().of(
          object().shape({
            texto: string().required('Obrigatório'),
          })
        ),
      })
    )
    .min(1, 'Deve ter ao menos cinco perguntas'),
});

const regras4 = object({
  dicas: array()
    .of(
      object().shape({
        titulo: string().required('Obrigatório'),
        url: string().url().required('Obrigatório'),
      })
    )
    .min(1, 'Deve ter ao menos uma dica'),
});

const regras5 = object({
  bibliografias: array()
    .of(
      object().shape({
        titulo: string().required('Obrigatório'),
        url: string().nullable().optional().url(),
      })
    )
    .min(1, 'Deve ter ao menos uma bibliográfia'),
});

const regras6 = object({
  resumo: mixed().test('obrigatorio', 'Obrigatório', (valor) => {
    if (valor?.blocks?.length && valor.blocks[0].text) {
      return true;
    }

    return false;
  }),
});

const ProducaoDeUA = ({ tarefa }: Props): JSX.Element => {
  const toast = useToast();

    const { data } = useRecuperarCategoriasQuery({
      finder: 'listaJson',
      limit: 100,
      sort: 'nome',
    });
  const [showModalDescrition, setShowModalDescription] =
    useState<boolean>(false);
  const [showModalQuizzes, setShowModalQuizzes] = useState<boolean>(false);
  const [suggestedDescription, setSuggestedDescription] = useState<string>('');
  const [suggestedSkills, setSuggestedSkills] = useState<string>('');
  const [suggestedQuizzes, setSuggestedQuizzes] = useState<Partial<Quiz>[]>();
  const [exibir, setExibir] = useState<boolean>(false);
  const [etapa, setEtapa] = useState<number>(1);
  const [editarTarefa, { isLoading }] = useEditarTarefaMutation();
  const [getUnitDescription, { isLoading: suggestingDescription }] =
    useGetUnitDescriptionMutation();
  const [getUnitSkills, { isLoading: suggestingSkills }] =
    useGetUnitSkillsMutation();
  const [getUnitQuizzes, { isLoading: suggestingQuizzes }] =
    useGetUnitQuizzesMutation();

  const usuario = useAppSelector((estado) => estado.autenticacao.usuario);
  const token = useAppSelector((estado) => estado.autenticacao.token);

  const tamanhoDoResumo = useMemo((): number => {
    let tamanhoDoResumo = 0;

    if (tarefa.dados?.resumo) {
      const texto = convertFromRaw(
        tarefa.dados?.resumo as unknown as RawDraftContentState
      );
      tamanhoDoResumo = texto.getPlainText('\u0001').length;
    }

    return tamanhoDoResumo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarefa]);

  const suggestDescription = async (): Promise<void> => {
    try {
      const descriptionResponse = await getUnitDescription({
        unit: tarefa.titulo,
      }).unwrap();

      setSuggestedDescription(descriptionResponse.eva);

      const skillsResponse = await getUnitSkills({
        unit: tarefa.titulo,
      }).unwrap();

      setSuggestedSkills(skillsResponse.eva);
    } catch (error) {
      toast({
        titulo: 'Erro ao sugerir',
        tema: 'danger',
        conteudo:
          'A EVA não conseguiu gerar as sugestões nesse momento. Verifique os logs para mais informações',
      });
    }
  };

  const acceptSuggestedDescription = async (): Promise<void> => {
    try {
      const data = {
        id: tarefa.id,
        dados: {
          ...tarefa.dados,
          descricao: {
            blocks: [
              {
                entityRanges: [],
                text: suggestedDescription,
                data: [],
                type: 'unstyled',
                inlineStyleRanges: [],
                depth: 0,
                key: '1e53d',
              },
            ],
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            entityMap: [],
          },
          competencias: {
            blocks: [
              {
                entityRanges: [],
                text: suggestedSkills,
                data: [],
                type: 'unstyled',
                inlineStyleRanges: [],
                depth: 0,
                key: '1e53d',
              },
            ],
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            entityMap: [],
          },
        },
      };

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      await editarTarefa(data).unwrap();

      setSuggestedDescription('');
      setSuggestedSkills('');
      setShowModalDescription(false);
    } catch (error) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const suggestQuizzes = async (): Promise<void> => {
    try {
      const quizzesResponse = await getUnitQuizzes({
        unit: tarefa.titulo,
      }).unwrap();

      const quizzes: Partial<Quiz>[] = [];

      quizzesResponse.eva.map((quiz) =>
        quizzes.push({
          enunciado: quiz.question,
          alternativas: quiz.alternatives.map(
            (alternative, index) =>
              ({
                correta: index == quiz.answer,
                texto: alternative,
              } as Alternativa)
          ),
        })
      );

      setSuggestedQuizzes(quizzes);
    } catch (error) {
      toast({
        titulo: 'Erro ao sugerir',
        tema: 'danger',
        conteudo:
          'A EVA não conseguiu gerar as sugestões nesse momento. Verifique os logs para mais informações',
      });
    }
  };

  const acceptSuggestedQuizzes = async (): Promise<void> => {
    try {
      const data = {
        id: tarefa.id,
        dados: {
          ...tarefa.dados,
          quizzes: suggestedQuizzes as Quiz[],
        },
      };

      await editarTarefa(data).unwrap();

      setSuggestedQuizzes([]);
      setShowModalQuizzes(false);
    } catch (error) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const calcularProgresso = (tarefa: Tarefa): number => {
    let progresso = 0;
    const fatia = 100 / 6;

    if (tarefa.dados?.powerpoint) {
      progresso += fatia;
    }
    if (tarefa.dados?.descricao && tarefa.dados.competencias) {
      progresso += fatia;
    }
    if (tarefa.dados?.quizzes && tarefa.dados.quizzes.length >= 5) {
      progresso += fatia;
    }
    if (tarefa.dados?.dicas) {
      progresso += fatia;
    }
    if (tarefa.dados?.bibliografias) {
      progresso += fatia;
    }
    if (tamanhoDoResumo >= 3000) {
      progresso += fatia;
    }

    progresso = Math.ceil(progresso);

    if (progresso > 100) {
      progresso = 100;
    }

    return progresso;
  };

  const enviarPowerPoint = async (
    dados: ProducaoDeUAInterface,
    form: FormikHelpers<FormikValues>
  ): Promise<void> => {
    try {
      const novosDados = {
        id: tarefa.id,
        dados: { ...tarefa.dados, powerpoint: dados.powerpoint },
      };
      await editarTarefa({
        ...novosDados,
        progresso: calcularProgresso(novosDados as Tarefa),
      }).unwrap();

      form.resetForm();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const removerPowerPoint = async (): Promise<void> => {
    try {
      const novosDados = {
        id: tarefa.id,
        dados: { ...tarefa.dados, powerpoint: '' },
      };
      await editarTarefa({
        ...novosDados,
        progresso: calcularProgresso(novosDados as Tarefa),
      }).unwrap();
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const enviarDescricao = async (
    dados: ProducaoDeUAInterface
  ): Promise<void> => {
    try {
      const novosDados = {
        id: tarefa.id,
        dados: {
          ...tarefa.dados,
          categoria_id: dados.categoria_id,
          descricao: dados.descricao,
          competencias: dados.competencias,
        },
      };
      await editarTarefa({
        ...novosDados,
        progresso: calcularProgresso(novosDados as Tarefa),
      }).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const enviarQuizzes = async (dados: ProducaoDeUAInterface): Promise<void> => {
    try {
      const novosDados = {
        id: tarefa.id,
        dados: {
          ...tarefa.dados,
          quizzes: dados.quizzes,
        },
      };
      await editarTarefa({
        ...novosDados,
        progresso: calcularProgresso(novosDados as Tarefa),
      }).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const enviarDicas = async (dados: ProducaoDeUAInterface): Promise<void> => {
    try {
      const novosDados = {
        id: tarefa.id,
        dados: {
          ...tarefa.dados,
          dicas: dados.dicas,
        },
      };
      await editarTarefa({
        ...novosDados,
        progresso: calcularProgresso(novosDados as Tarefa),
      }).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const enviarBibliografia = async (
    dados: ProducaoDeUAInterface
  ): Promise<void> => {
    try {
      const novosDados = {
        id: tarefa.id,
        dados: {
          ...tarefa.dados,
          bibliografias: dados.bibliografias,
        },
      };
      await editarTarefa({
        ...novosDados,
        progresso: calcularProgresso(novosDados as Tarefa),
      }).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const enviarResumo = async (dados: ProducaoDeUAInterface): Promise<void> => {
    try {
      const novosDados = {
        id: tarefa.id,
        dados: {
          ...tarefa.dados,
          resumo: dados.resumo,
        },
      };
      await editarTarefa({
        ...novosDados,
        progresso: calcularProgresso(novosDados as Tarefa),
      }).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const finalizar = async (): Promise<void> => {
    try {
      await editarTarefa({
        id: tarefa.id,
        status: 1,
      }).unwrap();

      toast({ conteudo: 'Finalizado com sucesso!', tema: 'success' });
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const validar = async (): Promise<void> => {
    try {
      await editarTarefa({ id: tarefa.id, validado: 1 }).unwrap();

      toast({
        conteudo: 'Validado com sucesso!',
        tema: 'success',
      });
    } catch (erro) {
      toast({
        titulo: 'Erro ao validar',
        conteudo: 'Verifique o registro de log para mais detalhes!',
        tema: 'danger',
      });
    }
  };

  return (
    <>
      <div className="alert alert-success">
        Tarefa atribuída ao professor conteudista
      </div>
      <div className="row">
        <div className="col-md-4">
          <ul className="list-group list-group-flush">
            <TarefaInformacoes tarefa={tarefa} />
            {['super', 'admin'].includes(usuario?.permissao || '') && (
              <div className="mt-4">
                <BotaoLink
                  ms
                  tema="outline-primary"
                  texto="Alterar Titulo da Tarefa"
                  className="w-100 shadow mb-3 ms-auto"
                  to={`/admin/tarefas/alterar-titulo/${tarefa.id}`}
                />
              </div>
            )}
            <li
              className={`list-group-item py-3 d-flex justify-content-between align-items-center cursor-pointer border-0 ${
                etapa == 1
                  ? 'bg-primary text-white rounded shadow-lg'
                  : 'bg-transparent'
              }`}
              onClick={() => setEtapa(1)}
            >
              <div>
                <strong>Apresentação em PowerPoint</strong>
                <small
                  className={`d-block ${
                    etapa == 1 ? 'text-light' : 'text-muted'
                  }`}
                >
                  Utilizado para gravação da aula
                </small>
              </div>
              {tarefa.dados?.powerpoint ? (
                <Icone
                  nome={etapa == 1 ? 'check-circle-fill' : 'check-circle'}
                  className="text-success fs-3"
                />
              ) : (
                <Icone
                  nome={etapa == 1 ? 'clock-fill' : 'clock'}
                  className="text-warning fs-3"
                />
              )}
            </li>
            <li
              className={`list-group-item py-3 d-flex justify-content-between align-items-center cursor-pointer border-0 ${
                etapa == 2
                  ? 'bg-primary text-white rounded shadow-lg'
                  : 'bg-transparent'
              }`}
              onClick={() => setEtapa(2)}
            >
              <div>
                <strong>Descrição da Unidade de Aprendizagem</strong>
                <small
                  className={`d-block ${
                    etapa == 2 ? 'text-light' : 'text-muted'
                  }`}
                >
                  Uma curta introdução ao conteúdo da aula
                </small>
              </div>
              {tarefa.dados?.descricao && tarefa.dados.competencias ? (
                <Icone
                  nome={etapa == 2 ? 'check-circle-fill' : 'check-circle'}
                  className="text-success fs-3"
                />
              ) : (
                <Icone
                  nome={etapa == 2 ? 'clock-fill' : 'clock'}
                  className="text-warning fs-3"
                />
              )}
            </li>
            <li
              className={`list-group-item py-3 d-flex justify-content-between align-items-center cursor-pointer border-0 ${
                etapa == 3
                  ? 'bg-primary text-white rounded shadow-lg'
                  : 'bg-transparent'
              }`}
              onClick={() => setEtapa(3)}
            >
              <div>
                <strong>Quiz interativo</strong>
                <small
                  className={`d-block ${
                    etapa == 3 ? 'text-light' : 'text-muted'
                  }`}
                >
                  Exercícios para fixação do conteúdo
                </small>
              </div>
              {tarefa.dados?.quizzes?.length &&
              tarefa.dados.quizzes.length >= 5 ? (
                <Icone
                  nome={etapa == 3 ? 'check-circle-fill' : 'check-circle'}
                  className="text-success fs-3"
                />
              ) : (
                <Icone
                  nome={etapa == 3 ? 'clock-fill' : 'clock'}
                  className="text-warning fs-3"
                />
              )}
            </li>
            <li
              className={`list-group-item py-3 d-flex justify-content-between align-items-center cursor-pointer border-0 ${
                etapa == 4
                  ? 'bg-primary text-white rounded shadow-lg'
                  : 'bg-transparent'
              }`}
              onClick={() => setEtapa(4)}
            >
              <div>
                <strong>Dicas+ para o aluno</strong>
                <small
                  className={`d-block ${
                    etapa == 4 ? 'text-light' : 'text-muted'
                  }`}
                >
                  Links para expandir o conhecimento
                </small>
              </div>
              {tarefa.dados?.dicas?.length ? (
                <Icone
                  nome={etapa == 4 ? 'check-circle-fill' : 'check-circle'}
                  className="text-success fs-3"
                />
              ) : (
                <Icone
                  nome={etapa == 4 ? 'clock-fill' : 'clock'}
                  className="text-warning fs-3"
                />
              )}
            </li>
            <li
              className={`list-group-item py-3 d-flex justify-content-between align-items-center cursor-pointer border-0 ${
                etapa == 5
                  ? 'bg-primary text-white rounded shadow-lg'
                  : 'bg-transparent'
              }`}
              onClick={() => setEtapa(5)}
            >
              <div>
                <strong>Referência Bibliográfica</strong>
                <small
                  className={`d-block ${
                    etapa == 5 ? 'text-light' : 'text-muted'
                  }`}
                >
                  Referências utilizadas para montar o conteúdo
                </small>
              </div>
              {tarefa.dados?.bibliografias?.length ? (
                <Icone
                  nome={etapa == 5 ? 'check-circle-fill' : 'check-circle'}
                  className="text-success fs-3"
                />
              ) : (
                <Icone
                  nome={etapa == 5 ? 'clock-fill' : 'clock'}
                  className="text-warning fs-3"
                />
              )}
            </li>
            <li
              className={`list-group-item py-3 d-flex justify-content-between align-items-center cursor-pointer border-0 ${
                etapa == 6
                  ? 'bg-primary text-white rounded shadow-lg'
                  : 'bg-transparent'
              }`}
              onClick={() => setEtapa(6)}
            >
              <div>
                <strong>Resumo</strong>
                <small
                  className={`d-block ${
                    etapa == 6 ? 'text-light' : 'text-muted'
                  }`}
                >
                  Utilizado para montar o livro-texto da aula
                </small>
              </div>
              {tamanhoDoResumo >= 3000 ? (
                <Icone
                  nome={etapa == 6 ? 'check-circle-fill' : 'check-circle'}
                  className="text-success fs-3"
                />
              ) : (
                <Icone
                  nome={etapa == 6 ? 'clock-fill' : 'clock'}
                  className="text-warning fs-3"
                />
              )}
            </li>
          </ul>
          {tarefa.progresso == 100 && tarefa.status == 0 && (
            <div className="mt-4">
              <Botao
                lg
                tema="secondary"
                texto="Finalizar tarefa"
                className="w-100 shadow"
                onClick={finalizar}
                confirmacao="Tem certeza que deseja finalizar essa tarefa?"
              />
            </div>
          )}
          {tarefa.status == 1 && tarefa.validado == 0 && (
            <div className="mt-4">
              <Botao
                lg
                tema="success"
                texto="Validar tarefa"
                icone="check-lg"
                className="w-100 shadow"
                onClick={validar}
                confirmacao="Tem certeza que deseja validar essa tarefa?"
              />
            </div>
          )}
          {tarefa.status == 1 &&
            tarefa.validado == 1 &&
            ['super', 'admin'].includes(usuario?.permissao || '') && (
              <div className="mt-4">
                <Botao
                  lg
                  tema="success"
                  texto="Criar tarefa baseada na atual"
                  className="w-100 shadow"
                  onClick={() => setExibir(true)}
                />
              </div>
            )}
        </div>
        <div className="col-md-8 mt-4 mt-sm-0">
          {etapa == 1 && (
            <div className="bg-white shadow-sm p-3 rounded">
              <h3 className="mb-0">Apresentação em PowerPoint</h3>
              <small className="d-block mb-3 text-muted">
                Essa apresentação será utilizada por você no momento em que
                gravar a sua aula no estúdio.
              </small>
              <Form regras={regras1} onSubmit={enviarPowerPoint}>
                {tarefa.dados?.powerpoint ? (
                  <div className="d-flex gap-2 flex-wrap">
                    <a
                      href={tarefa.dados?.powerpoint}
                      className="btn btn-primary btn-lg"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icone nome="projector" className="me-2" />
                      Visualizar apresentação
                    </a>
                    <Botao
                      lg
                      tema="danger"
                      texto="Enviar outra apresentação"
                      onClick={removerPowerPoint}
                      processando={isLoading}
                      className={
                        tarefa.status == 1 && usuario?.id == tarefa.atendente_id
                          ? 'disabled'
                          : ''
                      }
                    />
                  </div>
                ) : (
                  <>
                    <DropZone
                      name="powerpoint"
                      ajuda="As extensões permitidas são PPT e PPTX."
                    />
                    <Botao type="submit" texto="Salvar" />
                  </>
                )}
              </Form>
            </div>
          )}
          {etapa == 2 && (
            <div className="bg-white shadow-sm p-3 rounded">
              <div className="d-sm-flex gap-2 justify-content-between align-items-start">
                <div>
                  <h3 className="mb-0">Descrição da Unidade de Aprendizagem</h3>
                  <small className="d-block mb-3 text-muted">
                    Uma curta introdução explicando para o aluno o conteúdo que
                    ele encontrará e o que aprenderá.
                  </small>
                </div>
                <Botao
                  className="mb-3 mb-sm-3"
                  onClick={() => setShowModalDescription(true)}
                >
                  Sugestão da EVA
                </Botao>
              </div>
              <Form
                inicial={tarefa.dados}
                regras={regras2}
                onSubmit={enviarDescricao}
                desabilitado={
                  tarefa.status == 1 && usuario?.id == tarefa.atendente_id
                }
              >
                <div>
                  {usuario?.permissao !== 'conteudista' && (
                    <Selecao
                      name="categoria_id"
                      rotulo="Categoria"
                      opcoes={data?.categorias}
                      prefixar={<Icone nome="view-list" />}
                    />
                  )}
                </div>
                <Editor name="descricao" rotulo="Descrição" />
                <Editor name="competencias" rotulo="Competências adquiridas" />
                <Botao type="submit" texto="Salvar" />
              </Form>
            </div>
          )}
          {etapa == 3 && (
            <div className="bg-white shadow-sm p-3 rounded">
              <div className="d-sm-flex gap-2 justify-content-between align-items-start">
                <div>
                  <h3 className="mb-0">Quiz interativo</h3>
                  <small className="d-block mb-3 text-muted">
                    Exercícios para fixação do conteúdo
                  </small>
                </div>
                <Botao
                  className="mb-3 mb-sm-3"
                  onClick={() => setShowModalQuizzes(true)}
                >
                  Sugestão da EVA
                </Botao>
              </div>
              <Form
                inicial={tarefa.dados}
                regras={regras3}
                onSubmit={enviarQuizzes}
                desabilitado={
                  tarefa.status == 1 && usuario?.id == tarefa.atendente_id
                }
              >
                <FieldArray name="quizzes">
                  {({ push, remove }) => (
                    <>
                      <Contexto>
                        {({ values }) => (
                          <>
                            {Array.isArray(values.quizzes) &&
                              values.quizzes.map(
                                (quiz: Quiz, indice: number) => (
                                  <div
                                    key={indice.toString()}
                                    className="row mb-3"
                                  >
                                    <div className="col-md-6">
                                      <CaixaDeTexto
                                        name={`quizzes.${indice}.enunciado`}
                                        rotulo="Enunciado do quiz"
                                        altura={288}
                                      />
                                    </div>
                                    <div className="col-md-6">
                                      {quiz.imagem && (
                                        <img
                                          src={quiz.imagem}
                                          className="w-100 object-fit-cover rounded mb-3"
                                          style={{ maxHeight: 188 }}
                                        />
                                      )}
                                      <DropZone
                                        name={`quizzes.${indice}.imagem`}
                                        ajuda="Caso tenha uma imagem, faça o upload aqui."
                                      />
                                    </div>
                                    <div className="col-md-12">
                                      <FieldArray
                                        name={`quizzes.${indice}.alternativas`}
                                      >
                                        {() => (
                                          <>
                                            <div className="row align-items-center">
                                              <div className="col-1 mb-3">
                                                <CaixaDeSelecao
                                                  name={`quizzes.${indice}.alternativas.0.correta`}
                                                  semMargem
                                                />
                                              </div>
                                              <div className="col-11">
                                                <Campo
                                                  name={`quizzes.${indice}.alternativas.0.texto`}
                                                  rotulo="Alternativa #1"
                                                />
                                              </div>
                                            </div>
                                            <div className="row align-items-center">
                                              <div className="col-1 mb-3">
                                                <CaixaDeSelecao
                                                  name={`quizzes.${indice}.alternativas.1.correta`}
                                                  semMargem
                                                />
                                              </div>
                                              <div className="col-11">
                                                <Campo
                                                  name={`quizzes.${indice}.alternativas.1.texto`}
                                                  rotulo="Alternativa #2"
                                                />
                                              </div>
                                            </div>
                                            <div className="row align-items-center">
                                              <div className="col-1 mb-3">
                                                <CaixaDeSelecao
                                                  name={`quizzes.${indice}.alternativas.2.correta`}
                                                  semMargem
                                                />
                                              </div>
                                              <div className="col-11">
                                                <Campo
                                                  name={`quizzes.${indice}.alternativas.2.texto`}
                                                  rotulo="Alternativa #3"
                                                />
                                              </div>
                                            </div>
                                            <div className="row align-items-center">
                                              <div className="col-1 mb-3">
                                                <CaixaDeSelecao
                                                  name={`quizzes.${indice}.alternativas.3.correta`}
                                                  semMargem
                                                />
                                              </div>
                                              <div className="col-11">
                                                <Campo
                                                  name={`quizzes.${indice}.alternativas.3.texto`}
                                                  rotulo="Alternativa #4"
                                                />
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      </FieldArray>
                                    </div>
                                    <div className="col-md-1">
                                      <Botao
                                        tema="danger"
                                        icone="trash"
                                        onClick={() => remove(indice)}
                                      />
                                    </div>
                                  </div>
                                )
                              )}
                          </>
                        )}
                      </Contexto>
                      <div className="d-flex gap-2 justify-content-center">
                        <Botao
                          tema="success"
                          onClick={() =>
                            push({
                              enunciado: '',
                              alternativas: [
                                { correta: '', texto: '' },
                                { correta: '', texto: '' },
                                { correta: '', texto: '' },
                                { correta: '', texto: '' },
                              ],
                            })
                          }
                          icone="plus-lg"
                          texto="Adicionar outra"
                        />
                      </div>
                    </>
                  )}
                </FieldArray>
                <Botao type="submit" texto="Salvar" />
              </Form>
            </div>
          )}
          {etapa == 4 && (
            <div className="bg-white shadow-sm p-3 rounded">
              <h3 className="mb-0">Dicas+ para o aluno</h3>
              <small className="d-block mb-3 text-muted">
                Links para expandir o conhecimento
              </small>
              <Form
                inicial={tarefa.dados}
                regras={regras4}
                onSubmit={enviarDicas}
                desabilitado={
                  tarefa.status == 1 && usuario?.id == tarefa.atendente_id
                }
              >
                <FieldArray name="dicas">
                  {({ push, remove }) => (
                    <>
                      <Contexto>
                        {({ values }) => (
                          <>
                            {Array.isArray(values.dicas) &&
                              values.dicas.map(
                                (dica: DicaMais, indice: number) => (
                                  <div
                                    key={indice.toString()}
                                    className="row align-items-center"
                                  >
                                    <div className="col-md-6">
                                      <Campo
                                        name={`dicas.${indice}.titulo`}
                                        rotulo="Título da dica"
                                      />
                                    </div>
                                    <div className="col-md-5">
                                      <Campo
                                        name={`dicas.${indice}.url`}
                                        rotulo="Link para a dica"
                                      />
                                    </div>
                                    <div className="col-md-1">
                                      <Botao
                                        tema="danger"
                                        onClick={() => remove(indice)}
                                        icone="trash"
                                        className="mb-3"
                                      />
                                    </div>
                                  </div>
                                )
                              )}
                          </>
                        )}
                      </Contexto>
                      <div className="d-flex gap-2 justify-content-center">
                        <Botao
                          tema="success"
                          onClick={() => push({ titulo: '', url: '' })}
                          icone="plus-lg"
                          texto="Adicionar outra"
                        />
                      </div>
                    </>
                  )}
                </FieldArray>
                <Botao type="submit" texto="Salvar" />
              </Form>
            </div>
          )}
          {etapa == 5 && (
            <div className="bg-white shadow-sm p-3 rounded">
              <h3 className="mb-0">Referência Bibliográfica</h3>
              <small className="d-block mb-3 text-muted">
                Referências utilizadas para montar o conteúdo
              </small>
              <Form
                inicial={tarefa.dados}
                regras={regras5}
                onSubmit={enviarBibliografia}
                desabilitado={
                  tarefa.status == 1 && usuario?.id == tarefa.atendente_id
                }
              >
                <FieldArray name="bibliografias">
                  {({ push, remove }) => (
                    <>
                      <Contexto>
                        {({ values }) => (
                          <>
                            {Array.isArray(values.bibliografias) &&
                              values.bibliografias.map(
                                (
                                  bibliografia: Bibliografia,
                                  indice: number
                                ) => (
                                  <div
                                    key={indice.toString()}
                                    className="row align-items-center"
                                  >
                                    <div className="col-md-6">
                                      <Campo
                                        name={`bibliografias.${indice}.titulo`}
                                        rotulo="Título da obra"
                                      />
                                    </div>
                                    <div className="col-md-5">
                                      <Campo
                                        name={`bibliografias.${indice}.url`}
                                        rotulo="Link para a obra"
                                      />
                                    </div>
                                    <div className="col-md-1">
                                      <Botao
                                        tema="danger"
                                        onClick={() => remove(indice)}
                                        icone="trash"
                                        className="mb-3"
                                      />
                                    </div>
                                  </div>
                                )
                              )}
                          </>
                        )}
                      </Contexto>
                      <div className="d-flex gap-2 justify-content-center">
                        <Botao
                          tema="success"
                          onClick={() => push({ titulo: '', url: '' })}
                          icone="plus-lg"
                          texto="Adicionar outra"
                        />
                      </div>
                    </>
                  )}
                </FieldArray>
                <Botao type="submit" texto="Salvar" />
              </Form>
            </div>
          )}
          {etapa == 6 && (
            <div className="bg-white shadow-sm p-3 rounded">
              <h3 className="mb-0">Resumo da Unidade de Aprendizagem</h3>
              <small className="d-block mb-3 text-muted">
                O resumo inserido aqui, será utilizado para montar o
                livro-resumo para o aluno.
              </small>
              <Form
                inicial={tarefa.dados}
                regras={regras6}
                onSubmit={enviarResumo}
                desabilitado={
                  tarefa.status == 1 && usuario?.id == tarefa.atendente_id
                }
              >
                <Editor name="resumo" rotulo="Resumo" full />
                <p className="text-muted mb-0">
                  O tamanho mínimo do resumo deve ser de 3000 caracteres.
                </p>
                <div className="progress mb-3 position-relative">
                  <div
                    className="progress-bar"
                    role="progressbar"
                    aria-label="Example with label"
                    style={{ width: `${(tamanhoDoResumo * 100) / 3000}%` }}
                  ></div>
                  <span className="position-absolute top-50 start-50 translate-middle fw-bold">
                    {tamanhoDoResumo}/3000 caracteres enviados
                  </span>
                </div>
                <div className="d-flex gap-2">
                  <Botao type="submit" texto="Salvar" />
                  <BotaoLink
                    tema="secondary"
                    to={`/tarefas/livro/${tarefa.id}?token=${token}`}
                    icone="book-half"
                    texto="Pré-visualizar livro"
                    target="_blank"
                  />
                </div>
              </Form>
            </div>
          )}
        </div>
      </div>
      <Modal
        exibir={showModalDescrition}
        aoFechar={setShowModalDescription}
        titulo="Sugestão para descrição e competências"
      >
        <Form
          onSubmit={acceptSuggestedDescription}
          inicial={{
            descricao: suggestedDescription,
            competencias: suggestedSkills,
          }}
          desabilitado={suggestingDescription || suggestingSkills || isLoading}
        >
          <p>
            A EVA pode te dar uma ajuda sugerindo uma descrição e competências
            para essa UA se baseando no título dela. Para gerar a sugestão
            clique no botão &quot;gerar&quot; abaixo!
          </p>
          <>
            {suggestedDescription != '' && (
              <CaixaDeTexto name="descricao" rotulo="Descrição" />
            )}
            {suggestedSkills != '' && (
              <CaixaDeTexto
                name="competencias"
                rotulo="Competências adquiridas"
              />
            )}
            {suggestedDescription != '' && suggestedSkills != '' && (
              <p>
                Lembre-se que esse texto foi gerado por{' '}
                <strong>inteligência artificial</strong> e ainda{' '}
                <strong>precisa de revisão</strong>! Então antes de aceitar a
                sugestão,
                <strong>faça uma análise cuidadosa</strong>!
              </p>
            )}
          </>

          <div className="d-flex justify-content-end gap-2">
            <Botao
              onClick={suggestDescription}
              processando={suggestingDescription || suggestingSkills}
              tema="light"
            >
              Gerar sugestão
            </Botao>
            {suggestedDescription != '' && suggestedSkills != '' && (
              <Botao type="submit">Aceitar sugestão</Botao>
            )}
          </div>
        </Form>
      </Modal>
      <Modal
        lg
        exibir={showModalQuizzes}
        aoFechar={setShowModalQuizzes}
        titulo="Sugestão para quiz"
      >
        <Form
          onSubmit={acceptSuggestedQuizzes}
          inicial={{
            quizzes: suggestedQuizzes,
          }}
          desabilitado={suggestingQuizzes}
        >
          <p>
            A EVA pode te dar uma ajuda sugerindo uma lista de quiz para essa UA
            se baseando no título dela. Para gerar a sugestão clique no botão
            &quot;gerar&quot; abaixo!
          </p>
          <FieldArray name="quizzes">
            {({ remove }) => (
              <>
                <Contexto>
                  {({ values }) => (
                    <>
                      {Array.isArray(values.quizzes) &&
                        values.quizzes.map((quiz: Quiz, indice: number) => (
                          <div key={indice.toString()} className="row mb-3">
                            <div className="col-md-12">
                              <CaixaDeTexto
                                name={`quizzes.${indice}.enunciado`}
                                rotulo="Enunciado do quiz"
                                altura={150}
                              />
                            </div>
                            <div className="col-md-12">
                              <FieldArray
                                name={`quizzes.${indice}.alternativas`}
                              >
                                {() => (
                                  <>
                                    <div className="row align-items-center">
                                      <div className="col-1 mb-3">
                                        <CaixaDeSelecao
                                          name={`quizzes.${indice}.alternativas.0.correta`}
                                          semMargem
                                        />
                                      </div>
                                      <div className="col-11">
                                        <Campo
                                          name={`quizzes.${indice}.alternativas.0.texto`}
                                          rotulo="Alternativa #1"
                                        />
                                      </div>
                                    </div>
                                    <div className="row align-items-center">
                                      <div className="col-1 mb-3">
                                        <CaixaDeSelecao
                                          name={`quizzes.${indice}.alternativas.1.correta`}
                                          semMargem
                                        />
                                      </div>
                                      <div className="col-11">
                                        <Campo
                                          name={`quizzes.${indice}.alternativas.1.texto`}
                                          rotulo="Alternativa #2"
                                        />
                                      </div>
                                    </div>
                                    <div className="row align-items-center">
                                      <div className="col-1 mb-3">
                                        <CaixaDeSelecao
                                          name={`quizzes.${indice}.alternativas.2.correta`}
                                          semMargem
                                        />
                                      </div>
                                      <div className="col-11">
                                        <Campo
                                          name={`quizzes.${indice}.alternativas.2.texto`}
                                          rotulo="Alternativa #3"
                                        />
                                      </div>
                                    </div>
                                    <div className="row align-items-center">
                                      <div className="col-1 mb-3">
                                        <CaixaDeSelecao
                                          name={`quizzes.${indice}.alternativas.3.correta`}
                                          semMargem
                                        />
                                      </div>
                                      <div className="col-11">
                                        <Campo
                                          name={`quizzes.${indice}.alternativas.3.texto`}
                                          rotulo="Alternativa #4"
                                        />
                                      </div>
                                    </div>
                                  </>
                                )}
                              </FieldArray>
                            </div>
                            <div className="col-md-1">
                              <Botao
                                tema="danger"
                                icone="trash"
                                onClick={() => remove(indice)}
                              />
                            </div>
                          </div>
                        ))}
                    </>
                  )}
                </Contexto>
              </>
            )}
          </FieldArray>
          <>
            {suggestedQuizzes && suggestedQuizzes.length > 0 && (
              <p>
                Lembre-se que esse texto foi gerado por{' '}
                <strong>inteligência artificial</strong> e ainda{' '}
                <strong>precisa de revisão</strong>! Então antes de aceitar a
                sugestão,
                <strong>faça uma análise cuidadosa</strong>!
              </p>
            )}
          </>

          <div className="d-flex justify-content-end gap-2">
            <Botao
              onClick={suggestQuizzes}
              processando={suggestingQuizzes}
              tema="light"
            >
              Gerar sugestão
            </Botao>
            {suggestedQuizzes && suggestedQuizzes.length > 0 && (
              <Botao type="submit">Aceitar sugestão</Botao>
            )}
          </div>
        </Form>
      </Modal>
      <TarefaAdicionar exibir={exibir} aoFechar={setExibir} tarefa={tarefa} />
    </>
  );
};

export default ProducaoDeUA;
